import { useState, useMemo, useCallback, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useDebouncedValueNext } from 'common/hooks';
import { useClientsByAgentQuery } from 'api/queries/client';
import { escapedRegexString } from 'utils/regex';
import { getDisplayName } from 'utils/client';

function getInitials(client) {
  if (typeof client === 'string') {
    return client
      .split(/\s+/)
      .slice(0, 2)
      .map(w => w[0]?.toUpperCase())
      .join('');
  }
  const name = getDisplayName(client);
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase())
    .join('');
}

const avatarColors = ['#2E7D6F', '#5C6BC0', '#AB47BC', '#EF5350', '#FF7043', '#26A69A', '#7E57C2', '#42A5F5'];

const selectedClientBoxSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '8px',
  px: 1.5,
  py: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  transition: 'border-color 0.2s',
  '&:hover': { borderColor: 'text.secondary' },
};

const selectedAvatarSx = { width: 32, height: 32, fontSize: 13, fontWeight: 600 };

const clearButtonSx = { ml: 'auto', opacity: 0.5, '&:hover': { opacity: 1 } };

const optionRowSx = { display: 'flex', alignItems: 'center', gap: 1.5, py: 1 };

const optionAvatarSx = { width: 28, height: 28, fontSize: 12, fontWeight: 600 };

const newBadgeSx = {
  px: 0.75,
  py: 0.125,
  borderRadius: '4px',
  bgcolor: 'warning.light',
  color: 'warning.dark',
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1.4,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
};

function colorFromName(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

export default function ClientAutocomplete({ value, onChange, label = 'Client Name', size = 'small' }) {
  const { debouncedValue: term, setValue: setTerm } = useDebouncedValueNext('', 200);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const focusOnMountRef = useRef(false);

  const inputRefCallback = useCallback(node => {
    if (node && focusOnMountRef.current) {
      focusOnMountRef.current = false;
      node.focus();
    }
  }, []);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);
  const handleInputChange = useCallback(
    (_event, val) => {
      setInputValue(val);
      setTerm(val);
    },
    [setTerm],
  );
  const handleChange = useCallback(
    (_event, val) => {
      if (val) onChange(val);
    },
    [onChange],
  );
  const handleBlur = useCallback(() => {
    if (inputValue) onChange(inputValue);
  }, [inputValue, onChange]);
  const renderOption = useCallback((props, option) => {
    const { key: _key, ...rest } = props;
    const name = getDisplayName(option);
    const bg = colorFromName(name);
    return (
      <Box component="li" key={option.id} {...rest} sx={optionRowSx}>
        <Avatar sx={{ ...optionAvatarSx, bgcolor: bg }}>{getInitials(option)}</Avatar>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {name}
          </Typography>
          {option.email && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {option.email}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }, []);

  const where = useMemo(() => {
    if (!term) return {};
    return {
      or: [
        { name: { regexp: escapedRegexString(term) } },
        { fullName: { regexp: escapedRegexString(term) } },
        { email: { regexp: escapedRegexString(term) } },
      ],
    };
  }, [term]);

  const { data: options = [], isFetching } = useClientsByAgentQuery({
    options: {
      where,
      fields: ['id', 'name', 'fullName', 'email'],
      limit: 10,
      order: ['updatedDate DESC'],
    },
    queryOptions: {
      enabled: open,
      staleTime: 1000 * 60 * 2,
      keepPreviousData: true,
    },
  });

  if (value) {
    const isNew = typeof value === 'string';
    const displayLabel = isNew ? value : getDisplayName(value);
    const emailLabel = !isNew && value.email ? value.email : null;
    const bg = colorFromName(displayLabel);

    return (
      <Box sx={selectedClientBoxSx}>
        <Avatar sx={{ ...selectedAvatarSx, bgcolor: bg }}>{getInitials(value)}</Avatar>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {displayLabel}
            </Typography>
            {isNew && (
              <Typography variant="caption" sx={newBadgeSx}>
                New
              </Typography>
            )}
          </Stack>
          {emailLabel && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {emailLabel}
            </Typography>
          )}
        </Stack>
        <IconButton
          size="small"
          onClick={() => {
            if (isNew) {
              setInputValue(displayLabel);
              setTerm(displayLabel);
            } else {
              setInputValue('');
              setTerm('');
            }
            focusOnMountRef.current = true;
            setOpen(true);
            onChange(null);
          }}
          sx={clearButtonSx}
          aria-label={isNew ? 'Edit client name' : 'Clear client'}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Autocomplete
      freeSolo
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      filterOptions={x => x}
      options={options}
      loading={isFetching}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={handleBlur}
      getOptionLabel={option => (typeof option === 'string' ? option : getDisplayName(option))}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      renderOption={renderOption}
      renderInput={params => <TextField {...params} inputRef={inputRefCallback} label={label} size={size} fullWidth />}
    />
  );
}
